import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Background } from "../components/Background";
import { Chat } from "../components/Chat";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { ShieldLogo } from "../components/ui/Logo";
import { useSocket } from "../state/SocketContext";

export default function Lobby() {
  const navigate = useNavigate();
  const { room, matchStart, leaveRoom, setReady, socketId } = useSocket();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Bounce to home if there's no active room.
  useEffect(() => {
    if (!room) {
      const t = window.setTimeout(() => {
        if (!room) navigate("/");
      }, 600);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [room, navigate]);

  // Match has started — go to game screen.
  useEffect(() => {
    if (matchStart) navigate("/play/multi");
  }, [matchStart, navigate]);

  if (!room) {
    return (
      <Background>
        <Centered>
          <p className="text-white/70">Returning to lobby…</p>
        </Centered>
      </Background>
    );
  }

  const me = room.players.find((p) => p.socketId === socketId);
  const opponent = room.players.find((p) => p.socketId !== socketId);

  const toggleReady = async () => {
    if (!me) return;
    setBusy(true);
    await setReady(!me.ready);
    setBusy(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(room.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // best-effort
    }
  };

  const exit = async () => {
    await leaveRoom();
    navigate("/");
  };

  return (
    <Background>
      <div className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-4 py-10">
        <header className="mb-6 flex flex-col items-center gap-3 text-center">
          <ShieldLogo size={72} />
          <h1 className="font-display text-3xl uppercase tracking-[0.3em] text-white">
            Briefing Room
          </h1>
        </header>

        <Card glow className="w-full">
          <div className="mb-5 text-center">
            <div className="text-xs uppercase tracking-widest text-white/50">Room Code</div>
            <button
              onClick={copy}
              className="mt-1 inline-flex items-center gap-3 rounded-xl bg-white/5 px-5 py-2 font-display text-4xl tracking-[0.4em] text-cap-gold hover:bg-white/10"
              title="Click to copy"
            >
              {room.code}
              <span className="text-sm tracking-normal text-white/50">{copied ? "✓" : "⧉"}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <PlayerCard label="You" name={me?.name ?? "—"} ready={me?.ready ?? false} present={!!me} />
            <PlayerCard
              label="Opponent"
              name={opponent?.name ?? "Waiting…"}
              ready={opponent?.ready ?? false}
              present={!!opponent}
            />
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <Button
              variant={me?.ready ? "ghost" : "primary"}
              size="lg"
              block
              disabled={busy || !me}
              onClick={toggleReady}
            >
              {me?.ready ? "Stand Down" : "Ready Up"}
            </Button>
            <p className="text-center text-xs uppercase tracking-widest text-white/40">
              {opponent
                ? "Both players must ready up to start"
                : "Share the room code with your ally"}
            </p>
            <Button variant="ghost" size="md" block onClick={exit}>
              Leave Room
            </Button>
          </div>
        </Card>
      </div>
      <Chat collapsedDefault={false} />
    </Background>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 text-center">
      {children}
    </div>
  );
}

function PlayerCard({
  label,
  name,
  ready,
  present,
}: {
  label: string;
  name: string;
  ready: boolean;
  present: boolean;
}) {
  return (
    <div
      className={[
        "rounded-xl border-2 px-4 py-5 text-center transition-colors",
        present
          ? ready
            ? "border-emerald-400/60 bg-emerald-400/10"
            : "border-white/20 bg-white/5"
          : "border-dashed border-white/15 bg-transparent",
      ].join(" ")}
    >
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="mt-1 truncate font-display text-2xl text-white">{name}</div>
      <div
        className={[
          "mt-2 text-xs font-display uppercase tracking-widest",
          present
            ? ready
              ? "text-emerald-300"
              : "text-white/50"
            : "text-white/30",
        ].join(" ")}
      >
        {present ? (ready ? "Ready" : "Standing By") : "Awaiting Arrival"}
      </div>
    </div>
  );
}
